import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Eye, CheckCircle2, Sparkles, Users, Laptop, Wallet, Video, LifeBuoy, Network, Shield, Zap, Plug } from "lucide-react";

const MODULES = [
  { icon: Users, t: "Core HRM", d: "People directory · org chart · 18-tab employee profile · custom fields" },
  { icon: Video, t: "Time & Leave", d: "Attendance · leaves · shifts · approvals with multi-level routing" },
  { icon: Wallet, t: "Payroll + Claims", d: "Salary structures · payroll runs · PDF payslips · reimbursements" },
  { icon: Laptop, t: "Assets & ITSM", d: "Full asset lifecycle · helpdesk · SLA-driven tickets" },
  { icon: LifeBuoy, t: "Recruitment & Onboarding", d: "ATS · 6-stage Kanban · offer letters · branded onboarding" },
  { icon: Network, t: "Work Management", d: "Tasks · projects · board · timeline · calendar · workload" },
  { icon: Sparkles, t: "AI Copilot", d: "Claude-powered · answers with live company data · drafts letters" },
  { icon: Shield, t: "Compliance & Audit", d: "Audit log · RBAC · feature controls · branded white-label" },
];

const PLANS = [
  { name: "Starter", price: "₹199", per: "/employee/mo", desc: "For small teams getting started",
    features: ["Up to 50 employees", "Core HRM, Attendance, Leave", "Tasks + Documents", "Email support"],
    cta: "Start free trial", featured: false },
  { name: "Growth", price: "₹349", per: "/employee/mo", desc: "Most popular for growing companies",
    features: ["Up to 500 employees", "Everything in Starter", "Payroll + Claims + Helpdesk", "Recruitment + Performance", "AI Assistant (10k queries)", "Priority support"],
    cta: "Start free trial", featured: true },
  { name: "Enterprise", price: "Custom", per: "", desc: "Dedicated deployment with full branding",
    features: ["Unlimited employees", "Everything in Growth", "Dedicated infra / private cloud", "Custom modules & workflows", "SSO, SAML, audit exports", "SLA + white-glove onboarding"],
    cta: "Talk to sales", featured: false },
];

export default function Marketing() {
  return (
    <div className="min-h-screen bg-white">
      {/* Nav */}
      <header className="sticky top-0 z-20 backdrop-blur bg-white/80 border-b border-slate-200">
        <div className="max-w-6xl mx-auto flex items-center justify-between px-6 py-4">
          <Link to="/marketing" className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-md bg-slate-900 text-white flex items-center justify-center"><Eye className="w-4 h-4 text-cyan-400"/></div>
            <div className="font-[Outfit] font-semibold text-slate-900">QrewHR</div>
          </Link>
          <nav className="hidden md:flex items-center gap-6 text-sm text-slate-600">
            <a href="#modules" className="hover:text-slate-900">Modules</a>
            <a href="#why" className="hover:text-slate-900">Why QrewHR</a>
            <a href="#pricing" className="hover:text-slate-900">Pricing</a>
            <a href="#faq" className="hover:text-slate-900">FAQ</a>
          </nav>
          <div className="flex items-center gap-2">
            <Link to="/login"><Button variant="outline" size="sm">Sign in</Button></Link>
            <a href="#demo"><Button size="sm" className="bg-slate-900 hover:bg-slate-800">Try demo</Button></a>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-slate-900 via-slate-800 to-cyan-900/60"/>
        <div className="absolute inset-0 opacity-20" style={{ backgroundImage: "url('https://images.pexels.com/photos/36631700/pexels-photo-36631700.jpeg?auto=compress&w=2000')", backgroundSize: "cover" }}/>
        <div className="relative max-w-6xl mx-auto px-6 py-24 text-white">
          <div className="text-xs uppercase tracking-[0.3em] text-cyan-400 font-semibold mb-5">Your Smart HR Monitoring System</div>
          <h1 className="text-5xl md:text-6xl font-[Outfit] font-semibold leading-tight max-w-3xl">One platform to see, manage, and grow your people.</h1>
          <p className="mt-6 text-slate-300 text-lg max-w-2xl">QrewHR unifies HRMS, attendance, approvals, payroll, tasks, projects, assets, and AI insights into a single premium enterprise experience. Built for companies that want clarity without complexity.</p>
          <div className="mt-8 flex flex-wrap gap-3">
            <a href="#demo"><Button size="lg" className="bg-cyan-500 hover:bg-cyan-400 text-slate-900 font-semibold">Try the demo <Zap className="w-4 h-4 ml-1"/></Button></a>
            <a href="#pricing"><Button size="lg" variant="outline" className="border-white/20 text-white hover:bg-white/10">See pricing</Button></a>
            <a href="#enterprise"><Button size="lg" variant="ghost" className="text-white hover:bg-white/10">Enterprise deployment →</Button></a>
          </div>
          <div className="mt-10 grid grid-cols-3 md:grid-cols-4 gap-6 text-center">
            {[
              { n: "28", l: "Modules" },
              { n: "10+", l: "Roles" },
              { n: "AI", l: "Copilot" },
              { n: "API", l: "First" },
            ].map(s => (
              <div key={s.l} className="border border-white/10 rounded-md bg-white/5 p-4 backdrop-blur">
                <div className="text-3xl font-[Outfit] font-semibold text-cyan-400">{s.n}</div>
                <div className="text-xs uppercase tracking-wider text-slate-300 mt-1">{s.l}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Modules */}
      <section id="modules" className="py-24 bg-slate-50">
        <div className="max-w-6xl mx-auto px-6">
          <div className="max-w-2xl">
            <div className="text-xs uppercase tracking-[0.3em] font-semibold text-cyan-600">Everything in one place</div>
            <h2 className="mt-3 text-4xl font-[Outfit] font-semibold text-slate-900">28 modules. Zero context-switching.</h2>
            <p className="mt-3 text-slate-600">Stop gluing three tools with spreadsheets. QrewHR gives you HRMS, Payroll, Tasks, Assets, AI and Helpdesk in a single premium workspace.</p>
          </div>
          <div className="mt-10 grid md:grid-cols-2 lg:grid-cols-4 gap-4">
            {MODULES.map(m => (
              <Card key={m.t} className="p-5 border-slate-200 shadow-none hover:shadow-lg hover:-translate-y-0.5 transition-all bg-white">
                <div className="w-10 h-10 rounded-md bg-cyan-500/10 flex items-center justify-center mb-4"><m.icon className="w-5 h-5 text-cyan-600"/></div>
                <div className="font-[Outfit] font-semibold text-slate-900">{m.t}</div>
                <p className="text-[13px] text-slate-600 mt-1">{m.d}</p>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Why */}
      <section id="why" className="py-24">
        <div className="max-w-6xl mx-auto px-6 grid md:grid-cols-2 gap-12 items-start">
          <div>
            <div className="text-xs uppercase tracking-[0.3em] font-semibold text-cyan-600">Why teams pick QrewHR</div>
            <h2 className="mt-3 text-4xl font-[Outfit] font-semibold text-slate-900">Built for how enterprises actually operate.</h2>
            <p className="mt-3 text-slate-600">QrewHR is designed for the flow of a real operating company — from CEO dashboards to employee payslips, from leave approvals to offer letters, from asset recovery to performance goals.</p>
          </div>
          <div className="space-y-4">
            {[
              "Role-based permissions across 10+ personas — nothing leaks.",
              "Every module is a toggle. Super Admin shapes the product per company.",
              "AI Copilot that answers with live org data (not generic fluff).",
              "Audit log on every action. SOC-ready out of the box.",
              "White-label branding — your logo, your colors, your domain.",
              "API-first. Integrations for Google Workspace, Slack, biometric, more.",
            ].map(t => (
              <div key={t} className="flex items-start gap-3">
                <CheckCircle2 className="w-5 h-5 text-emerald-500 mt-0.5 shrink-0"/>
                <span className="text-slate-700">{t}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section id="pricing" className="py-24 bg-slate-50">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center max-w-2xl mx-auto">
            <div className="text-xs uppercase tracking-[0.3em] font-semibold text-cyan-600">Pricing</div>
            <h2 className="mt-3 text-4xl font-[Outfit] font-semibold text-slate-900">Simple, predictable, fair.</h2>
            <p className="mt-3 text-slate-600">Start free for 14 days · cancel anytime · no hidden fees.</p>
          </div>
          <div className="mt-12 grid md:grid-cols-3 gap-5">
            {PLANS.map(p => (
              <Card key={p.name} className={`p-6 border ${p.featured ? "border-cyan-500 ring-2 ring-cyan-500/20 bg-white" : "border-slate-200 bg-white"} shadow-none relative`}>
                {p.featured && <div className="absolute -top-3 left-6 text-[10px] uppercase tracking-wider bg-cyan-500 text-white px-2 py-1 rounded">Most popular</div>}
                <div className="text-slate-900 font-[Outfit] font-semibold text-xl">{p.name}</div>
                <div className="mt-4 flex items-end gap-1">
                  <span className="text-4xl font-[Outfit] font-semibold text-slate-900">{p.price}</span>
                  {p.per && <span className="text-slate-500 text-sm mb-1">{p.per}</span>}
                </div>
                <p className="text-sm text-slate-500 mt-2">{p.desc}</p>
                <ul className="mt-5 space-y-2 text-sm text-slate-700">
                  {p.features.map(f => <li key={f} className="flex items-start gap-2"><CheckCircle2 className="w-4 h-4 text-emerald-500 mt-0.5 shrink-0"/>{f}</li>)}
                </ul>
                <Link to="/login" className="block mt-6"><Button className={`w-full ${p.featured ? "bg-cyan-500 hover:bg-cyan-400 text-slate-900" : "bg-slate-900 hover:bg-slate-800"}`}>{p.cta}</Button></Link>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Demo */}
      <section id="demo" className="py-24">
        <div className="max-w-4xl mx-auto px-6">
          <Card className="p-10 border-slate-200 shadow-none bg-gradient-to-br from-slate-50 to-cyan-50/40">
            <div className="text-center">
              <div className="text-xs uppercase tracking-[0.3em] font-semibold text-cyan-600">Try before you buy</div>
              <h3 className="mt-3 text-3xl font-[Outfit] font-semibold text-slate-900">Instant demo sandbox.</h3>
              <p className="mt-3 text-slate-600">No signup. No credit card. Jump into a seeded company with 15 employees, live payroll, attendance data, and a working AI assistant.</p>
              <div className="mt-6 flex flex-wrap gap-3 justify-center">
                <Link to="/login"><Button size="lg" className="bg-slate-900 hover:bg-slate-800">Launch demo →</Button></Link>
                <Button size="lg" variant="outline" onClick={() => window.location.href = "mailto:sales@hreye.com?subject=HR%20Eye%20demo%20request"}>Request guided demo</Button>
              </div>
              <div className="mt-5 text-xs text-slate-500">Admin: <code className="bg-white px-1.5 rounded border border-slate-200">arjun@hreye.com</code> · Password: <code className="bg-white px-1.5 rounded border border-slate-200">password123</code></div>
            </div>
          </Card>
        </div>
      </section>

      {/* Enterprise */}
      <section id="enterprise" className="py-24 bg-slate-900 text-white">
        <div className="max-w-5xl mx-auto px-6 grid md:grid-cols-2 gap-12 items-center">
          <div>
            <div className="text-xs uppercase tracking-[0.3em] font-semibold text-cyan-400">Enterprise Edition</div>
            <h2 className="mt-3 text-4xl font-[Outfit] font-semibold">Own your deployment. Own your data.</h2>
            <p className="mt-3 text-slate-300">For organizations that need dedicated infra, private cloud, custom workflows, or complete white-label branding. We ship QrewHR Enterprise as a dedicated instance with your domain, your branding, your integrations, and your SLA.</p>
            <div className="mt-6 flex gap-3">
              <Button size="lg" onClick={() => window.location.href = "mailto:enterprise@hreye.com?subject=Enterprise%20deployment"} className="bg-cyan-500 hover:bg-cyan-400 text-slate-900">Talk to enterprise sales</Button>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            {[
              { icon: Shield, t: "Private infra" },
              { icon: Plug, t: "Custom integrations" },
              { icon: Sparkles, t: "Custom AI model" },
              { icon: Users, t: "Unlimited seats" },
            ].map(f => (
              <div key={f.t} className="p-4 border border-white/10 bg-white/5 rounded-md">
                <f.icon className="w-5 h-5 text-cyan-400"/>
                <div className="mt-2 font-medium text-sm">{f.t}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section id="faq" className="py-24">
        <div className="max-w-4xl mx-auto px-6">
          <div className="text-center"><div className="text-xs uppercase tracking-[0.3em] font-semibold text-cyan-600">FAQ</div><h3 className="mt-3 text-3xl font-[Outfit] font-semibold text-slate-900">Everything you wanted to ask.</h3></div>
          <div className="mt-10 space-y-3">
            {[
              ["Is QrewHR a SaaS or self-hosted?", "Both. Start on our SaaS and graduate to Enterprise (dedicated) whenever you need full control."],
              ["Which modules are included?", "All 28 modules ship with the product. Super Admin toggles what each organization actually needs."],
              ["How does pricing work?", "Per-employee pricing with a 14-day free trial. Enterprise is custom."],
              ["Do you support SSO?", "Google OAuth out of the box; SAML/Okta in Enterprise."],
              ["Is there an API?", "Yes — every module exposes REST endpoints. API docs available on request."],
            ].map(([q, a]) => (
              <Card key={q} className="p-5 border-slate-200 shadow-none">
                <div className="font-medium text-slate-900">{q}</div>
                <div className="mt-1 text-sm text-slate-600">{a}</div>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-slate-200 py-10 bg-white">
        <div className="max-w-6xl mx-auto px-6 flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-2 text-slate-600 text-sm">
            <Eye className="w-4 h-4 text-cyan-600"/><span>QrewHR · Your Smart HR Monitoring System</span>
          </div>
          <div className="text-xs text-slate-500">© {new Date().getFullYear()} QrewHR · All rights reserved</div>
        </div>
      </footer>
    </div>
  );
}
